
<template>
	<div v-if="products_count > 0" ref="scrollingElement">
		<!-- Filters -->
		<aside :class="['filters-toolbar', { 'filters-toolbar-sticky': filterSticky }]">
			<div class="collection-filters">
				<!-- Sort By -->
				<div v-if="!noProductsFound && sortOptions.length" class="filter-group" v-click-outside="hideSortBy">
					<div :class="['filter-button', 'filter-toggle', 'btn', 'btn-secondary', { 'active': sortByOpen }]"
						@click="sortByOpen = !sortByOpen; filterByOpen = false">
						<span class="filter-title">{{ $language.collection.sort_by }}</span>
					</div>
					<!-- Dropdown -->
					<TransitionExpand>
						<div class="filter-dropdown" v-show="sortByOpen">
							<div
								v-for="(sortOption, index) in sortOptions"
								v-if="!(template !== 'search' && sortOption.handle == 'relevance')"
								:key="index"
								@click=" toggleFilter('sort by', sortOption.handle, collectionID)"
								:class="['sort-option', showActive(sortOption.handle)]">
								<span class="sort-option-title">
									{{ sortOption.label }}
								</span>
							</div>
						</div>
					</TransitionExpand>
				</div>

				<!-- Filter By -->
				<div v-if="filtersToShow.length" class="filter-group" v-click-outside="hideFilterBy">
					<div :class="['filter-button', 'filter-toggle', 'btn', 'btn-secondary', { 'active': filterByOpen }]"
						@click=" filterByOpen = !filterByOpen; sortByOpen = false">
						<span class="filter-title">{{ $language.collection.filter_by }}</span>
					</div>
					<!-- Dropdown -->
					<TransitionExpand>
						<div class="filter-dropdown filter-dropdown-filters" v-show="filterByOpen">
							<div v-for="(filterGroup, index) in filtersToShow" :key="index"
								:class="['filter-group-child', { 'two-columns': filterGroup.values.length >= 10 }]">
								<div
									:class="['filter-title', 'filter-toggle', { 'active': filtersOpen.includes(index) }]"
									@click="toggleFilterBtn(index); sortByOpen = false"
									v-text="filterGroup.label"></div>
								<TransitionExpand :onlyMobile="true" :isMobile="!$screen.large_up">
									<div class="filter-dropdown-child" v-show="filtersOpen.includes(index) || $screen.large_up">
										<div v-if="filterGroup.filterType == 'collection'" class="inner">
											<div v-for="(option, index) in filterGroup.values" :key="index"
												class="filter-option">
												<a
													:href="'/collections/' + option.handle"
													class="filter-option-title"
													v-text="option.label"></a>
											</div>
										</div>
										<div v-else class="inner">
											<div
												v-for="(option, index) in filterGroup.values"
												:key="index"
												@click="toggleFilter(option.key, stripKey(option.key), collectionID, filterGroup.filterOptionId, $event)"
												:class="['filter-option', showActive(option.key)]">
												<div class="filter-option-title">
													{{ stripKey(option.key, filterGroup.prefix) }}
												</div>
											</div>
										</div>
									</div>
								</TransitionExpand>
							</div>
							<div v-if="filtersApplied.length" @click=" clearFilters()" class="collection-clear-filters">
								<IconClose />{{ $language.collection.clear_filters }}
							</div>
						</div>
					</TransitionExpand>
				</div>
			</div>

			<div class="view-filters">
				<!-- Image toggle -->
				<div class="image-toggle">
					<div @click=" updateImageView('outfit')" :class="['image-view-toggle', { 'active': imageView == 'outfit' }]">
						<div class="image-view-title">{{ $language.collection.outfit_view }}</div>
					</div>
					<div @click=" updateImageView('product')" :class="['image-view-toggle', { 'active': imageView == 'product' }]">
						<div class="image-view-title">{{ $language.collection.product_view }}</div>
					</div>
				</div>

				<!-- Grid toggle -->
				<div class="view-toggle">
					<div @click=" updateGridSize('small')" :class="['small-view-toggle', { 'active': gridSize == 'small' }]"
						:title="$language.collection.small_view">
						<div class="view-box"></div>
						<div class="view-box"></div>
						<div class="view-box"></div>
						<div class="view-box"></div>
					</div>
					<div @click=" updateGridSize('large')" :class="['large-view-toggle', { 'active': gridSize == 'large' }]"
						:title="$language.collection.large_view">
						<div class="view-box"></div>
						<div class="view-box"></div>
					</div>
				</div>
			</div>
		</aside>

		<!-- Product grid -->
		<section :class="['product-grid', { 'large-view': gridSize == 'large' }, { 'product-grid-sticky': filterSticky }]" v-if="!noProductsFound">
			<template v-for="(item, idx) in products">
				<!-- Product item -->
				<ProductItem
					v-if="item.id"
					:key="item.id"
					:product="item"
					:collection="handle"
					:similar-styles="similarStyles"
					@styling="similarStyling" />

				<!-- Promo block -->
				<PromoItem
					v-else
					:key="`${item.insertPosition}-${idx}`"
					:data="item" />
			</template>

			<!-- Campaign Item -->
			<div v-if="isCampaign"
				class="product-grid-campaign">
				<a :href="campaign_product.url">
					<div class="product-image">
						<img
							:src="campaignImage"
							:srcset="campaignImageSrcset"
							sizes="(min-width: 2000px) 950px, (min-width: 990px) calc(50vw - 50px), (min-width: 870px) calc(50vw - 25px), calc(100vw - 40px)"
							:alt="campaign_product.title">
					</div>
					<div class="product-text">
						<p class="title" v-text="campaign_product.title"></p>
						<p v-if="campaign_product.vendor" class="vendor" v-text="campaign_product.vendor"></p>
						<p v-if="campaign_product.compare_at_price" class="price sale">
							<span itemprop="price" v-text="campaign_product.price"></span>
							<s v-text="campaign_product.compare_at_price"></s>
						</p>
						<p v-else class="price">
							<span itemprop="price" v-text="campaign_product.price"></span>
						</p>
					</div>
				</a>
			</div>
		</section>

		<!-- Pagination -->
		<infinite-loading
			v-if="!noProductsFound"
			class="collection-products-infinite-loading"
			:identifier="infiniteId"
			@infinite="infiniteHandler">
			<div slot="no-more"></div>
			<div slot="no-results"></div>
			<div slot="spinner">
				<div class="site-loading active">
					<div class="spinner">
						<div class="circle"></div>
					</div>
				</div>
			</div>
		</infinite-loading>
	</div>

	<!-- No products -->
	<div v-else class="no-results">
		<p v-if="template === 'search'">{{ $language.search.no_results_message }}</p>
		<p v-else>{{ $language.collection.no_matches }}</p>
	</div>
</template>

<script>
import ProductItem from '../Components/ProductItem';
import PromoItem from '../Components/PromoItem';
import InfiniteLoading from 'vue-infinite-loading';
import IconClose from '../Icons/IconClose';
import TransitionExpand from '../Transitions/TransitionExpand';

export default {
	props: [
		'id',
		'handle',
		'title',
		'products_count',
		'template',
		'query',
		'sort_order',
		'campaign_image',
		'campaign_product',
		'exclude_enable',
		'exclude_collection_ids',
		'promo_blocks',
	],

	components: { ProductItem, PromoItem, InfiniteLoading, IconClose, TransitionExpand },

	data() {
		return {
			collectionID: this.id,
			products: [],
			filterOptions: [], // The filters returned by boost commerce API
			filterByOpen: false, // The 'filter by' dropdown is open
			filtersOpen: [], // The filters which are open inside of the 'filter by' dropdown
			filterTagsApplied: [], // The current filters entire tags applied (colour: blue, colour: red)
			filtersApplied: [], // The current filters tag values applied (blue, red)
			sortOptions: [],
			sortByOpen: false, // The 'sort by' dropdown is open
			sortByApplied: '', // The current sort by value applied
			page: 1,
			infiniteId: +new Date(),
			noProductsFound: null,
			allProductsLoaded: false,
			totalProducts: 0,
			imageView: 'outfit',
			gridSize: 'small',
			urlWithFilter: location.pathname,
			/**
			 * We've got this set up to HIDE all products tagged with "Lifecycle: Sale" in ALL collections now (so that sale products don't show up in their regular collections, eg Dresses). However, they are launching sale tomorrow so they want to SHOW products tagged with "Lifecycle: Sale" ONLY IN the collections with the collection IDS below (so the sale products DO show up in sale collections, but not in any other collection). - Lance
			 * I don't know what PFS do, I just grab the code form their theme and implement them here. - Leo
			 */
			// excludeIds: [],
			similarStyles: null,
			excludeIds: this.exclude_collection_ids,
			filterSticky: false,
		}
	},

	computed: {
		isCampaign() {
			return this.campaign_image && this.campaign_product.url && this.products_count >= 10 && this.gridSize == 'small';
		},

		campaignImage() {
			let image = this.campaign_image;
			image += 'w_300,h_221,c_fill';
			return image;
		},

		campaignImageSrcset() {
			let image = this.campaign_image,
				imageSrcset = ''

			imageSrcset += image
			imageSrcset += 'w_300,h_221,c_fill 300w, '

			imageSrcset += image
			imageSrcset += 'w_500,h_368,c_fill 500w, '

			imageSrcset += image
			imageSrcset += 'w_750,h_552,c_fill 750w, '

			imageSrcset += image
			imageSrcset += 'w_950,h_699,c_fill 950w, '

			imageSrcset += image
			imageSrcset += 'w_1500,h_1104,c_fill 1500w, '

			imageSrcset += image
			imageSrcset += 'w_1900,h_1398,c_fill 1900w'

			return imageSrcset
		},

		// Only show filters if more than one value (stops 'Shoes' filter on 'Shoes' category etc)
		filtersToShow() {
			let filtersToShowList = []

			this.filterOptions.forEach(filter => {
				if (filter.values.length > 1) {
					filtersToShowList.push(filter)
				}
			})

			return filtersToShowList
		},

		paginationLimit() {
			// Make sure the first page has all grid spots filled
			let specialGridSpots = 0;

			if (this.isCampaign) {
				specialGridSpots += 2;
			}

			if (this.promo_blocks && this.promo_blocks.length) {
				specialGridSpots += this.promo_blocks.length;
			}

			return 24 - specialGridSpots % 4;
		},
	},

	mounted() {
		this.loadCollection()

		if (window.localStorage.getItem('collection_image_view')) {
			this.imageView = window.localStorage.getItem('collection_image_view')
		}

		if (window.localStorage.getItem('collection_view')) {
			this.gridSize = window.localStorage.getItem('collection_view')
		}

		// Similiar Styling Configs
		let data = document.querySelector('script#SimilarStyling')
		if (data) this.similarStyles = JSON.parse(data.innerText);

		window.addEventListener('scroll', () => {
			const rect = this.$refs.scrollingElement.getBoundingClientRect();
			const elementTop = rect.top;
			let headerHeight = 79;
			if (window.innerWidth > 989) {
				headerHeight = 64;
			}
			if (elementTop <= headerHeight) {
				this.filterSticky = true;
			} else {
				this.filterSticky = false;
			}
		});
	},

	methods: {
		async loadCollection() {
			window.sessionStorage.removeItem('similar_styling')

			// get url with params - filters
			let params = location.search.includes('?filter=') ? '?filter=' + location.search.split('?filter=')[1] : ''
			let filterArray = [];
			this.urlWithFilter = location.pathname + params;

			if (location.search.includes('?filter=')) {
				const filters = location.search.split('?filter=')[1].replace('%20&%20', '::').replaceAll('%3D', '=').replaceAll('%3A', ':');
				filterArray = filters.split('&');
				filterArray.forEach(filter => {
					this.filterTagsApplied.push(filter.replace('::', ' & ').replaceAll('%20', ' ').replaceAll('%3A', ':').replace('=', '|||'));
					this.filtersApplied.push(filter.replace('::', ' & ').replaceAll('%20', ' ').replaceAll('%3A', ':').split('=')[1]);
				})
			}

			await this.getFilteredCollection({ collectionID: this.id, page: 1 })

			if (filterArray) {
				filterArray.forEach(filter => {
					let title = filter.split('=')[0].split('_'),
						value = filter.split('=')[1];

					document.querySelectorAll('.filter-title.filter-toggle').forEach(item => {
						const filter = item.textContent.replace(/(\n\t|\n|\t)/gm, "");
						if (filter.toLowerCase() === title[title.length - 1]) {
							item.nextElementSibling.querySelectorAll('.filter-option').forEach(option => {
								const activeOption = option.textContent.replace(/(\n\t|\n|\t)/gm, "");

								if (value === activeOption) option.classList.add('active')
							})
						}
					})
				})
			}

			// Build sorting
			// If theme settings retrieved
			if ('general' in theme.filterSettings) {
				theme.filterSettings.general.customSortingList.split('|').forEach(sortOption => {
					this.sortOptions.push({
						handle: sortOption,
						label: theme.filterSettings.label.sortByOptions[sortOption]
					})
				})
			}

			this.sortByApplied = this.sort_order

			this.products.length ? (this.page = 1, this.noProductsFound = false) : this.noProductsFound = true
		},

		toggleFilterBtn(idx) {
			if (this.filtersOpen.includes(idx)) {
				let index = this.filtersOpen.indexOf(idx)
				this.filtersOpen.splice(index, 1)
			} else {
				this.filtersOpen.push(idx)
			}
		},

		toggleFilter(tag, value, collectionID, filterOptionId) {
			// Reset products
			this.products = [];
			const filterParams = this.urlWithFilter === location.pathname ? `?filter=${filterOptionId}=${tag}` : `&${filterOptionId}=${tag}`;

			if (!this.filtersApplied.includes(tag)) {
				// Add filter
				if (tag == 'sort by') {
					this.sortByApplied = value
				} else {
					this.filterTagsApplied.push(`${filterOptionId}|||${tag}`)
					this.filtersApplied.push(tag)

					this.urlWithFilter = this.urlWithFilter + filterParams;
					history.pushState('', '', this.urlWithFilter);
				}
			} else {

				// first else last
				let replaceWord = '';
				if (this.filterTagsApplied.length === 1) {
					replaceWord = `?filter=${filterOptionId}=${tag}`;
				} else {
					this.filterTagsApplied.forEach((item, index) => {
						if (item.includes(tag)) {
							if (index === 0) {
								replaceWord = `${filterOptionId}=${tag}&`;
							} else {
								replaceWord = `&${filterOptionId}=${tag}`;
							}
						}
					})
				}
				this.urlWithFilter = this.urlWithFilter.replace('%20&%20', ' & ').replaceAll('%20', ' ').replaceAll('%3A', ':').replace(replaceWord, '');
				history.pushState('', '', this.urlWithFilter);

				// Remove filter
				this.filterTagsApplied.splice(this.filterTagsApplied.indexOf(`${filterOptionId}|||${tag}`), 1)
				this.filtersApplied.splice(this.filtersApplied.indexOf(tag), 1)
			}

			this.getFilteredCollection({ collectionID: this.id, page: 1 })
			this.page = 1
		},

		clearFilters() {
			this.filterTagsApplied = []
			this.filtersApplied = []
			this.products = []
			this.getFilteredCollection({ collectionID: this.id, page: 1 })
			this.page = 1
		},

		stripKey(tag, prefix) {
			if (/:(.+)/.exec(tag) && /:(.+)/.exec(tag).length) {
				tag = /:(.+)/.exec(tag)[1].trim()
			}

			if (prefix) {
				let prefixStripped = prefix.replace('\\-', '-')
				return tag.replace(prefixStripped, '').trim()
			} else {
				return tag
			}
		},

		showActive(option) {
			if (this.filtersApplied.includes(option)) return 'active'
			if (this.sortByApplied.includes(option)) return 'active'
		},

		async getFilteredCollection({ collectionID, page }) {
			// BCSF Endpoint
			let url = new URL('https://services.mybcapps.com/bc-sf-filter/')

			// Request options
			let params = {
				shop: theme.domain,
				page: page,
				limit: this.paginationLimit,
				product_available: true,
				variant_available: true,
				build_filter_tree: true,
				collection_scope: collectionID
			};

			// Collection vs Search
			if (this.query) {
				url.pathname = url.pathname + 'search'
				params.q = this.query
			} else {
				url.pathname = url.pathname + 'filter'
			}

			// Set sort order
			if (this.sortByApplied) {
				params.sort = this.sortByApplied;
			} else {
				params.sort = this.sort_order;
			}

			// Create query string from params object
			Object.keys(params).forEach(key => {
				url.searchParams.append(key, params[key])
			})

			// Insert filters
			if (this.filterTagsApplied.length) {
				// let tags = []
				this.filterTagsApplied.forEach(filter => {
					let filterKey = filter.split('|||')[0]
					let filterVal = filter.split('|||')[1]
					url.searchParams.append(`${filterKey}[]`, filterVal)
				})
			}

			// Send request
			await fetch(url)
				.then(response => response.json())
				.then(data => {
					let products = data.products

					if (products.constructor === Object) {
						// 0 products to show
						this.products = []
					} else {
						// Splice promo blocks into products array
						if (this.page === 1) {
							const total_product = data.total_product;
							let newContent = `${total_product} results for ”${this.query}”`;
							if (total_product == 0) {
								newContent = `0 result for ”${this.query}”`;
							} else if (total_product == 1) {
								newContent = `1 result for ”${this.query}”`;
							}
							const ele = document.querySelector('.search-header .results-count');
							if (ele) {
								ele.textContent = newContent;
								ele.classList.remove('display-none');
							}
							this.promo_blocks?.forEach(block => {
								const insertPosition = Number(block.insertPosition) - 1;
								products.splice(insertPosition, 0, block);
							})
						}

						// Show products
						this.products.push(...products)
						this.allProductsLoaded = products.length < this.paginationLimit;
						this.totalProducts = data.total_product
						// Build filter tree
						this.filterOptions = data.filter.options.filter(group => group.values.length)
						// Activate paginator
						this.infiniteId += 1
					}
				})
				.catch(error => {
					console.log('error', error)
				})
		},

		infiniteHandler: function ($state) {
			setTimeout(async function () {
				this.page += 1
				await this.getFilteredCollection({ collectionID: this.id, page: this.page })
				if (this.allProductsLoaded) {
					$state.complete()
				} else {
					$state.loaded()
				}
			}.bind(this), 1000) // Let the initial loadCollection function run first
		},

		updateImageView(view) {
			this.imageView = view
			window.localStorage.setItem('collection_image_view', view)
		},

		updateGridSize(size) {
			this.gridSize = size
			window.localStorage.setItem('collection_view', size)
		},

		hideSortBy() {
			this.sortByOpen = false
		},

		hideFilterBy() {
			this.filterByOpen = false
		},

		similarStyling: function (e) {
			// check collection
			if (this.id !== "") {
				window.sessionStorage.setItem('similar_styling', JSON.stringify(e))
				window.location.href = '/collections/all'
				return
			}
			this.filtersApplied = []
			this.filterTagsApplied = []
			for (let x of e.params) {
				let k = x['key'], v = x['value']
				this.filterTagsApplied.push(`${k}|||${v}`)
				this.filtersApplied.push(v)
			}
			// Reset products
			this.products = []
			this.getFilteredCollection({ collectionID: this.id, page: 1 })
			this.page = 1
		},
	}
}
</script>
